import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Attaches a cryptographically-verifiable Entra access token to NEST API calls
 * as `Authorization: Bearer <token>`. The Function validates this token's
 * signature/issuer/audience and derives the user's identity from it — replacing
 * the previously trusted (spoofable) X-User-Email header.
 */
export const appInsightsInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Only our API calls need the token (direct Function URL or SWA /api/* proxy).
  if (!req.url.includes('/api/')) {
    return next(req);
  }

  // Best-effort telemetry label. The Function only uses this when there is NO
  // verified token AND enforcement is off — never for an access-control decision.
  // The verified Bearer token (below) always takes precedence.
  const email = authService.getCurrentUserEmail();

  return authService.getApiToken().pipe(
    switchMap((token) => {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        // Diagnostic: a missing token here is why the Function returns 401 when
        // AUTH_REQUIRED=true. If this warns but the token later appears valid,
        // the issue is timing; if the token IS attached and the API still 401s,
        // the issue is Function-side validation (audience/scope/expose-API).
        console.warn('[Auth] No bearer token available for', req.url);
      }
      if (email && email !== 'unknown') {
        headers['X-User-Email'] = email;
      }
      if (Object.keys(headers).length) {
        req = req.clone({ setHeaders: headers });
      }
      return next(req);
    })
  );
};
